// src/pages/ELearning/CertificatePrintPage.tsx — ФИЧА O: печать сертификата A4
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Printer, ShieldCheck, ArrowLeft } from 'lucide-react';

const CertificatePrintPage: React.FC = () => {
  const { number } = useParams<{ number: string }>();
  const [cert, setCert] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!number) { setError('Номер не указан'); setLoading(false); return; }
    fetch(`/api/elearning/certificates/public/${encodeURIComponent(number)}`)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.certificate) setCert(d.certificate);
        else setError(d.error || 'Сертификат не найден');
      })
      .catch(() => setError('Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [number]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <Loader2 size={40} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
    </div>;
  }

  if (error || !cert) {
    return <div style={{ textAlign: 'center', padding: 80 }}>
      <h2 style={{ color: '#ef4444' }}>{error || 'Сертификат не найден'}</h2>
      <a href="/certificate/verify" style={{ color: '#667eea', marginTop: 20, display: 'inline-block' }}>
        ← Вернуться к проверке
      </a>
    </div>;
  }

  const dateStr = cert.completedAt
    ? new Date(cert.completedAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .cert-page { box-shadow: none !important; margin: 0 !important; }
        }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .cert-page { animation: fadeUp 0.5s ease-out; }
      `}</style>

      {/* ─── Кнопки управления (не печатаются) ─── */}
      <div className="no-print" style={styles.topbar}>
        <a href={`/certificate/verify/${number}`} style={styles.backBtn}>
          <ArrowLeft size={16} /> К проверке
        </a>
        <button onClick={() => window.print()} style={styles.printBtn}>
          <Printer size={16} /> Печать / PDF
        </button>
      </div>

      {/* ─── A4 Landscape сертификат ─── */}
      <div className="cert-page" style={styles.page}>
        {/* Двойная рамка */}
        <div style={styles.borderGold} />
        <div style={styles.borderBlue} />

        {/* Углы-декор */}
        <div style={{ ...styles.corner, top: 20, left: 20, borderTop: '3px solid #c9a227', borderLeft: '3px solid #c9a227' }} />
        <div style={{ ...styles.corner, top: 20, right: 20, borderTop: '3px solid #c9a227', borderRight: '3px solid #c9a227' }} />
        <div style={{ ...styles.corner, bottom: 20, left: 20, borderBottom: '3px solid #c9a227', borderLeft: '3px solid #c9a227' }} />
        <div style={{ ...styles.corner, bottom: 20, right: 20, borderBottom: '3px solid #c9a227', borderRight: '3px solid #c9a227' }} />

        {/* Контент */}
        <div style={styles.content}>
          {/* Заголовок */}
          <div style={styles.brand}>
            <ShieldCheck size={20} color="#c9a227" />
            <span style={styles.brandText}>HR LAMA · E-Learning</span>
          </div>

          <h1 style={styles.title}>СЕРТИФИКАТ</h1>
          <div style={styles.titleLine} />

          <p style={styles.subtitle}>Настоящий сертификат выдан</p>

          <h2 style={styles.userName}>{cert.userName}</h2>

          <div style={styles.nameLine} />

          <p style={styles.subtitle2}>за успешное прохождение теста</p>

          <h3 style={styles.testTitle}>«{cert.testTitle}»</h3>

          {cert.testCategory && (
            <p style={styles.category}>Категория: {cert.testCategory}</p>
          )}

          <div style={styles.scoreBox}>
            <div style={styles.scoreValue}>{cert.score}%</div>
            <div style={styles.scoreLabel}>результат</div>
          </div>

          {/* Нижний блок */}
          <div style={styles.bottom}>
            <div style={styles.bottomLeft}>
              <div style={styles.bottomLabel}>Дата выдачи</div>
              <div style={styles.bottomValue}>{dateStr}</div>
              <div style={{ ...styles.bottomLabel, marginTop: 12 }}>Номер сертификата</div>
              <div style={styles.bottomNumber}>{cert.number}</div>
            </div>

            <div style={styles.bottomRight}>
              <div style={styles.companyName}>{cert.companyName || 'HR LAMA'}</div>
              <div style={styles.signatureLine} />
              <div style={styles.bottomLabel}>подпись руководителя</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  topbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', marginBottom: 20,
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '10px 18px', background: 'rgba(255,255,255,0.9)',
    color: '#5a5f7a', borderRadius: 12, textDecoration: 'none',
    fontWeight: 700, fontSize: 13,
    border: '1px solid rgba(0,0,0,0.08)',
  },
  printBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '12px 22px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', border: 'none', borderRadius: 12,
    fontWeight: 700, fontSize: 14, cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
  },

  page: {
    position: 'relative',
    width: '297mm', height: '210mm',   // A4 landscape
    maxWidth: '100%',
    margin: '0 auto 40px',
    background: '#fafbfc',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
  borderGold: {
    position: 'absolute', top: 15, left: 15, right: 15, bottom: 15,
    border: '3px solid #c9a227', borderRadius: 4, pointerEvents: 'none',
  },
  borderBlue: {
    position: 'absolute', top: 25, left: 25, right: 25, bottom: 25,
    border: '1px solid #1e3a5f', borderRadius: 4, pointerEvents: 'none',
  },
  corner: {
    position: 'absolute', width: 40, height: 40, pointerEvents: 'none',
  },
  content: {
    position: 'relative', padding: '60px 80px',
    height: '100%', display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center',
  },

  brand: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
  },
  brandText: {
    fontSize: 11, fontWeight: 800, color: '#c9a227',
    letterSpacing: 2, textTransform: 'uppercase',
  },

  title: {
    fontSize: 56, fontWeight: 900, color: '#1e3a5f',
    margin: 0, letterSpacing: 3,
  },
  titleLine: {
    width: 100, height: 3, background: '#c9a227',
    margin: '12px 0 20px',
  },

  subtitle: {
    fontSize: 14, color: '#666', margin: 0,
    fontStyle: 'italic',
  },

  userName: {
    fontSize: 44, fontWeight: 900, color: '#c9a227',
    margin: '16px 0 8px', letterSpacing: 1,
  },
  nameLine: {
    width: 280, height: 2, background: '#c9a227',
    margin: '0 0 20px',
  },

  subtitle2: {
    fontSize: 14, color: '#666', margin: 0,
  },

  testTitle: {
    fontSize: 28, fontWeight: 700, color: '#1e3a5f',
    margin: '12px 0 4px', maxWidth: '80%',
  },
  category: {
    fontSize: 12, color: '#888', margin: '4px 0 0',
    textTransform: 'uppercase', letterSpacing: 1,
  },

  scoreBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    marginTop: 20,
  },
  scoreValue: {
    fontSize: 42, fontWeight: 900, color: '#10b981',
    lineHeight: 1,
  },
  scoreLabel: {
    fontSize: 10, color: '#888', textTransform: 'uppercase',
    letterSpacing: 2, marginTop: 4,
  },

  bottom: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    width: '100%', marginTop: 'auto', paddingTop: 24,
  },
  bottomLeft: { textAlign: 'left' },
  bottomRight: { textAlign: 'right' },
  bottomLabel: {
    fontSize: 10, color: '#888', textTransform: 'uppercase',
    letterSpacing: 1, marginBottom: 4,
  },
  bottomValue: { fontSize: 14, fontWeight: 600, color: '#1a1a2e' },
  bottomNumber: {
    fontSize: 16, fontWeight: 800, color: '#c9a227',
    fontFamily: 'monospace', letterSpacing: 1,
  },
  companyName: {
    fontSize: 18, fontWeight: 800, color: '#1e3a5f',
    marginBottom: 8,
  },
  signatureLine: {
    width: 180, height: 1, background: '#666',
    marginLeft: 'auto', marginBottom: 4,
  },
};

export default CertificatePrintPage;
